import * as React from 'react';
import { BarChart } from 'components/BarChart';
import { ApiContext } from 'context/ApiContext';
import QueryParams from 'context/QueryParams';
import {
  MainChartContainer,
  NoDataContainer,
  SpendByChartContainer,
  Wrapper,
  MapWrapper,
} from './Analysis.styles';
import { CONTRIBUTIONS, CHART_TYPES_CONSTANTS } from 'utils/constants';
import {
  ICurrencyFormat,
  ISettingsResponse,
  ISpendByType,
  ISpendContribution,
  IHeadlinesResponse,
  IDisplayByType,
} from 'utils/types';
import PersonnelMap from 'components/PersonnelMap';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import isEqual from 'lodash/isEqual';
import { createSpendByChartData, getCategories } from './utils';

const OTHERS = 'Others';

interface ISpendByContainerProps {
  analysisLoading?: boolean;
  chartRef?: any;
  chartOption: string;
  contributions?: string;
  displayBy: IDisplayByType;
  downloadSpendData: (o: { displayBy: IDisplayByType }) => Promise<any>;
  format: ICurrencyFormat;
  headlines: IHeadlinesResponse;
  getSpendAnalysisData: () => void;
  loading: boolean;
  onExportChart: (c: any, f: string) => void;
  permanentContractorChecked: boolean;
  selectedSpendBy?: string;
  serviceTowerL2?: string;
  setStateToLocalStorage: () => void;
  settings?: ISettingsResponse;
  showSuccessNotification: () => void;
  spendBy: ISpendByType;
  spendItems: ISpendContribution[];
  updateAppState: (p: { [key: string]: any }, v?: () => void) => void;
  params?: { [key: string]: string | string[] };
}

interface ISpendByContainerState {
  categories: any[];
  series: any[];
}

class SpendByContainer extends React.Component<
  ISpendByContainerProps,
  ISpendByContainerState
> {
  state = {
    categories: [],
    series: [],
  };

  onBarClick = (category?: string) => {
    if (!category || category === OTHERS) {
      return;
    }
    const { getSpendAnalysisData, selectedSpendBy, spendBy } = this.props;
    if (selectedSpendBy && spendBy === ISpendByType.SERVICE_TOWER) {
      this.props.updateAppState(
        { serviceTowerL2: category },
        getSpendAnalysisData
      );
    } else {
      this.props.updateAppState(
        { selectedSpendBy: category },
        getSpendAnalysisData
      );
    }
  };

  onCountryClick = (category?: string, callback?: () => void) => {
    const { getSpendAnalysisData, updateAppState } = this.props;

    updateAppState({ selectedSpendBy: category }, () => {
      getSpendAnalysisData();

      if (callback) {
        callback();
      }
    });
  };

  getChartData = () => {
    const {
      displayBy,
      headlines,
      permanentContractorChecked,
      selectedSpendBy,
      spendBy,
      spendItems,
    } = this.props;

    if (!headlines || !spendItems) {
      return { categories: [], series: [] };
    }
    const { revenue } = headlines.currentPeriod;
    const isPersonnel = this.isPersonnel();
    const { categories, otherCategories } = getCategories({
      displayBy,
      selectedSpendBy,
      spendItems,
    });
    return createSpendByChartData({
      categories,
      displayBy,
      otherCategories,
      isPersonnel,
      permanentContractorChecked,
      revenue,
      selectedSpendBy,
      spendBy,
      spendItems,
    });
  };

  isPersonnel = () => {
    return this.props.contributions === CONTRIBUTIONS.PERSONNEL;
  };

  getChartWidth = (categoriesLength: number) => {
    if (categoriesLength > 20) {
      return categoriesLength * 60;
    }
    return document.body.clientWidth - 80;
  };

  componentDidMount() {
    this.setState({
      ...this.state,
      ...this.getChartData(),
    });
  }

  componentDidUpdate(prevProps: ISpendByContainerProps) {
    if (!this.props.analysisLoading && !isEqual(prevProps, this.props)) {
      this.setState({ ...this.getChartData() });
    }
  }

  render() {
    const { categories, series } = this.state;
    const {
      chartOption,
      chartRef,
      displayBy,
      format,
      loading,
      permanentContractorChecked,
      spendBy,
      spendItems,
      selectedSpendBy,
      serviceTowerL2,
    } = this.props;
    if (loading) {
      return <LoadingIndicator loading />;
    }
    const isMapView = chartOption === CHART_TYPES_CONSTANTS.MAP;
    const isNoData = !loading && spendItems.length === 0 && !isMapView;

    if (isNoData) {
      return (
        <NoDataContainer>
          <h4>No Data Available for this combination of filters</h4>
          <br />
          <h6>Please try to reset or change the filter options</h6>
        </NoDataContainer>
      );
    }
    const selectedCategory =
      spendBy === ISpendByType.SERVICE_TOWER ? serviceTowerL2 : selectedSpendBy;
    return (
      <Wrapper>
        <SpendByChartContainer>
          {isMapView ? (
            <MapWrapper>
              <PersonnelMap
                onCountryClick={this.onCountryClick}
                displayType={displayBy}
                selectedSpendBy={selectedSpendBy}
                permanentContractorChecked={permanentContractorChecked}
              />
            </MapWrapper>
          ) : (
            <MainChartContainer>
              <BarChart
                categories={categories}
                chartRef={chartRef}
                chartWidth={this.getChartWidth(categories.length)}
                displayType={displayBy}
                format={format}
                onClick={this.onBarClick}
                permanentContractorChecked={permanentContractorChecked}
                selectedCategory={selectedCategory}
                series={series}
                type={chartOption}
              />
            </MainChartContainer>
          )}
        </SpendByChartContainer>
      </Wrapper>
    );
  }
}

export default (props: any) => (
  <QueryParams>
    {({ params }) => (
      <ApiContext.Consumer>
        {({ downloadSpendData, format, settings }) => (
          <SpendByContainer
            {...props}
            downloadSpendData={downloadSpendData}
            format={format}
            params={params}
            settings={settings}
          />
        )}
      </ApiContext.Consumer>
    )}
  </QueryParams>
);
